import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, BarChart3, BookOpen, Brain, ClipboardCheck, Database, FolderTree, HelpCircle, ListChecks, Plus,
  Sparkles, Upload,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppButton, AppCard, PageHeader, StatsCard, StatsGrid } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import AddQuestionsDialog from "./AddQuestionsDialog";
import QuizCategoryFormModal from "./QuizCategoryFormModal";
import SetupSteps from "./SetupSteps";
import { canUseQuizAi, currentUsertype, setupSteps, splitCategories } from "./quizBankRules";

/** Everything the quiz module holds, each with the one thing people go there to do. */
const AREAS = [
  {
    key: "bank", icon: HelpCircle, title: "Question bank", route: "/quiz-bank",
    text: "Every question, filed by category and subtopic. Make and organise the topics here too.",
  },
  {
    key: "passages", icon: BookOpen, title: "Passages and cases", route: "/quiz-bank/passages",
    text: "A paragraph, chart or PDF that several questions are asked about.",
  },
  {
    key: "upload", icon: Upload, title: "Upload questions", route: "/quiz-bank/bulk-upload",
    text: "Bring in a CSV, Excel or JSON file of questions in one go.",
  },
  {
    key: "assessments", icon: ClipboardCheck, title: "Assessments", route: "/assessments",
    text: "Quizzes, class tests and placement exams built from the bank.",
    action: { label: "New assessment", route: "/assessments/create" },
  },
  {
    key: "series", icon: ListChecks, title: "Test series", route: "/test-series",
    text: "A run of tests with progress and a leaderboard — NEET, EAMCET, aptitude.",
    action: { label: "New series", route: "/test-series/create" },
  },
  {
    key: "analytics", icon: BarChart3, title: "Analytics", route: "/assessment-analytics",
    text: "Attempts, scores and how each question performs.",
  },
  {
    key: "bulk", icon: Database, title: "Bulk operations", route: "/assessment-bulk",
    text: "Import, export and archive assessments in bulk.",
  },
];

/** One area of the module. The title is the link (its hit area is the whole
 *  card); a second action sits above it. */
function AreaCard({ area, onOpen }) {
  const Icon = area.icon;
  return (
    <AppCard as="li" hover className="tw-relative tw-gap-2 tw-p-4 focus-within:tw-shadow-focus">
      <span aria-hidden="true" className="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary-soft tw-text-primary">
        <Icon size={18} />
      </span>
      <button
        type="button"
        onClick={() => onOpen(area.route)}
        className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-semibold tw-text-content focus:tw-outline-none after:tw-absolute after:tw-inset-0 after:tw-content-['']"
      >
        {area.title} <ArrowRight size={14} aria-hidden="true" className="tw-text-content-subtle" />
      </button>
      <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">{area.text}</p>
      {area.action && (
        <div className="tw-relative tw-z-10 tw-mt-auto tw-pt-1">
          <AppButton variant="outline" size="sm" onClick={() => onOpen(area.action.route)}>
            <Plus size={14} aria-hidden="true" /> {area.action.label}
          </AppButton>
        </div>
      )}
    </AppCard>
  );
}

/**
 * QuizCenterPage — the quiz module's home (`/quiz-center`).
 *
 * It leads with the four steps from nothing to a published test — organise
 * the topics, add questions, build an assessment, give it to students — each
 * ticked off from what already exists, the next one carrying its buttons. Once
 * all four are done the steps fold away behind "Show the setup steps", and the
 * page is the counts and a way into every area.
 */
export default function QuizCenterPage() {
  const navigate = useNavigate();
  const canUseAi = useMemo(() => canUseQuizAi(currentUsertype()), []);
  const [counts, setCounts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showSteps, setShowSteps] = useState(false);
  const [adding, setAdding] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const load = useCallback(() => {
    const allBranches = { headers: { "X-Branch-Id": "all" } };
    return Promise.all([
      apiClient.get("/quiz-questions/filters", allBranches).catch(() => ({ data: {} })),
      apiClient.get("/quiz-categories", { params: { flat: true }, ...allBranches }).catch(() => ({ data: { categories: [] } })),
      apiClient.get("/assessments", { params: { pageSize: 1 }, ...allBranches }).catch(() => ({ data: {} })),
      apiClient.get("/assessments", { params: { pageSize: 1, status: "published" }, ...allBranches }).catch(() => ({ data: {} })),
    ]).then(([q, c, a, p]) => {
      const flat = c.data.categories || [];
      setCategories(flat);
      setCounts({
        questions: q.data.totalQuestions || 0,
        categories: splitCategories(flat).top.length,
        assessments: a.data.total || (a.data.assessments || []).length || 0,
        published: p.data.total || 0,
      });
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const setup = setupSteps(counts || {});
  const allDone = counts && setup.current === -1;
  const stepActions = {
    topics: (
      <>
        <AppButton size="sm" onClick={() => setCategoryOpen(true)}>
          <FolderTree size={14} aria-hidden="true" /> Create a category
        </AppButton>
        <AppButton size="sm" variant="outline" onClick={() => navigate("/quiz-bank")}>Open the question bank</AppButton>
      </>
    ),
    questions: (
      <AppButton size="sm" onClick={() => setAdding(true)}>
        <Plus size={14} aria-hidden="true" /> Add questions
      </AppButton>
    ),
    assessment: (
      <AppButton size="sm" onClick={() => navigate("/assessments/create")}>
        <Sparkles size={14} aria-hidden="true" /> Build an assessment
      </AppButton>
    ),
    publish: (
      <AppButton size="sm" onClick={() => navigate("/assessments")}>
        <ClipboardCheck size={14} aria-hidden="true" /> Open assessments
      </AppButton>
    ),
  };

  const value = (n) => (counts ? n.toLocaleString() : "—");

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <PageHeader
        icon={Brain}
        title="Quiz Center"
        description="Your question bank, assessments and results."
        actions={(
          <>
            <AppButton variant="outline" onClick={() => setAdding(true)}>
              <Plus size={16} aria-hidden="true" /> Add questions
            </AppButton>
            <AppButton onClick={() => navigate("/assessments/create")}>
              <Sparkles size={16} aria-hidden="true" /> New assessment
            </AppButton>
          </>
        )}
      />

      {counts && (!allDone || showSteps) && (
        <SetupSteps
          title={allDone ? "Setup steps" : "Get set up in four steps"}
          description={allDone
            ? "All four are done. Come back here whenever you start a new subject."
            : "Do them in order. Each one ticks itself off as you go."}
          steps={setup.steps}
          current={setup.current}
          actions={stepActions}
          headerAction={allDone && (
            <AppButton variant="ghost" size="sm" onClick={() => setShowSteps(false)}>Hide</AppButton>
          )}
        />
      )}

      <StatsGrid>
        <StatsCard icon={HelpCircle} title="Questions in the bank" value={value(counts?.questions || 0)} iconColor="blue" />
        <StatsCard icon={FolderTree} title="Categories" value={value(counts?.categories || 0)} iconColor="cyan" />
        <StatsCard icon={ClipboardCheck} title="Assessments" value={value(counts?.assessments || 0)} iconColor="emerald" />
        <StatsCard icon={BarChart3} title="Published" value={value(counts?.published || 0)} iconColor="amber" />
      </StatsGrid>

      <section aria-labelledby="quiz-areas" className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2">
          <h2 id="quiz-areas" className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Everything in the quiz module</h2>
          {allDone && !showSteps && (
            <AppButton variant="ghost" size="sm" onClick={() => setShowSteps(true)}>Show the setup steps</AppButton>
          )}
        </div>
        <ul className="tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0 tw-grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
          {AREAS.map((a) => <AreaCard key={a.key} area={a} onOpen={navigate} />)}
        </ul>
      </section>

      <AddQuestionsDialog
        show={adding}
        onHide={() => setAdding(false)}
        base="/quiz-bank"
        canUseAi={canUseAi}
        categories={categories}
        onAiSaved={(message) => { setToast({ show: true, message, type: "success" }); load(); }}
      />

      <QuizCategoryFormModal
        show={categoryOpen}
        onHide={() => setCategoryOpen(false)}
        onSaved={() => {
          setCategoryOpen(false);
          setToast({ show: true, message: "Category created", type: "success" });
          load();
        }}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
}
